import type { EventBus } from "../core/EventBus.ts";
import type { Card } from "../entities/types.ts";

export const MAX_PAYLOADS = 3;

/**
 * PayloadRack — holds armed payload cards until the player deploys them.
 * Pure logic, no DOM. EventBus-driven, unit-testable.
 */
export class PayloadRack {
  private armed: Card[] = [];
  private bus: EventBus;

  constructor(bus: EventBus) {
    this.bus = bus;
  }

  get isFull(): boolean {
    return this.armed.length >= MAX_PAYLOADS;
  }

  get count(): number {
    return this.armed.length;
  }

  /**
   * BL-56b — the rack "charge": armed payloads / capacity (0..1). The rack has
   * no separate cooldown timer — charge IS how full it is — so this is the live
   * payload-charge read for the Pro-Bar. O(1), render-only (no sim coupling).
   */
  get chargeRatio(): number {
    return this.armed.length / MAX_PAYLOADS;
  }

  get payloads(): readonly Card[] {
    return this.armed;
  }

  /** Arm a payload card into the rack. Returns true on success. */
  arm(card: Card): boolean {
    if (this.isFull) return false;
    this.armed.push(card);
    this.bus.emit("payload.armed", { card });
    return true;
  }

  /** Deploy (fire) the payload at the given index. Returns the card or null. */
  deploy(index: number): Card | null {
    if (index < 0 || index >= this.armed.length) return null;
    const card = this.armed.splice(index, 1)[0]!;
    this.bus.emit("payload.fired", { card });
    return card;
  }

  /** Set of card IDs currently armed (for draw-pool exclusion). */
  armedIds(): Set<string> {
    return new Set(this.armed.map((c) => c.id));
  }

  /** Clear rack state (run-reset discipline). */
  reset(): void {
    this.armed = [];
  }
}
